import { useEffect, useState } from 'react';

import { CampaignModel, ProfileModel } from '@pukka/api';
import { campaignApi, profileApi } from '@pukka/api/queries';
import { queryClient } from '@pukka/tanstack-query';
import { withPersist, proxy, proxyMap } from '@pukka/valtio';
import { useQuery } from '@tanstack/react-query';

import { getCampaignStatus } from './utils';

type ApiStatus = ReturnType<typeof profileApi.useProfile>['status'];

interface DetailedCampaignImage {
  id: number;
  url: string;
  name: string;
  width?: number;
  height?: number;
}

type DetailedCampaign = CampaignModel.Detailed;

interface DetailedCampaignWithStatus extends DetailedCampaign {
  status: CampaignModel.Status;
}

type DetailedCampaignWithStatusAndLoadingTrue = Partial<DetailedCampaignWithStatus> & {
  isLoading: true;
};

type DetailedCampaignWithStatusAndLoadingFalse = DetailedCampaignWithStatus & {
  isLoading: false;
};

type DetailedCampaignWithStatusAndLoading = DetailedCampaignWithStatusAndLoadingTrue | DetailedCampaignWithStatusAndLoadingFalse;

interface CustomerDisplayPersistedState extends Record<string, unknown> {
  campaignIds: Set<number>;
  campaignNames: Map<number, string>;
  status: CampaignModel.StatusSelection;
}

interface CustomerDisplayState {
  page: number;
  selectedOutletId: ProfileModel.Outlet['id'];
  campaigns: Map<number, DetailedCampaignWithStatusAndLoading>;
  creatingCampaigns: Map<string, DetailedCampaignWithStatusAndLoading>;
  canFetchMore: boolean;
  isLoading: boolean;
  modalData?: {
    outletIds: number[];
    errorOutlets: number[];
    mappedErrorOutlets: (string | undefined)[];
    handleChangeOutlets: (outlets: Set<ProfileModel.Outlet>) => void;
  };
  readonly onFetchMore: () => void;
  readonly onChangeSelectedOutletId: (outlet: ProfileModel.Outlet) => void;
  readonly onCreateCampaign: (
    data: CampaignModel.CreateRequest,
    handler?: (data: CampaignModel.CreateRequest) => Promise<DetailedCampaign>,
  ) => Promise<DetailedCampaign>;
  readonly onDeactivateCampaign: <T extends DetailedCampaign['id']>(
    id: T,
    handler?: (data: T, isActive: boolean) => Promise<void>,
  ) => Promise<void>;
  readonly onDeleteCampaign: <T extends DetailedCampaign['id']>(id: T, handler?: (data: T) => Promise<void>) => Promise<void>;
  readonly onUpdateCampaign: (
    data: CampaignModel.EditRequest,
    handler?: (data: CampaignModel.EditRequest) => Promise<DetailedCampaign>,
  ) => Promise<DetailedCampaign>;
  readonly onReactivateCampaign: <T extends DetailedCampaign['id']>(
    id: T,
    handler?: (data: T, isActive: boolean) => Promise<void>,
  ) => Promise<void>;
  readonly onReactivateAndUpdateCampaign: (data: CampaignModel.EditRequest) => Promise<DetailedCampaign>;
  readonly onChangeFilter: (status: CampaignModel.StatusSelection) => void;
  readonly useData: () => {
    data: DetailedCampaign[];
    isFetched: boolean;
    status: ApiStatus;
  };
  readonly useOutletDetails: (outletIds: number[]) => {
    status: ApiStatus;
    data: (ProfileModel.Outlet | undefined)[];
  };
}

const PER_PAGE = 50;

const { CAMPAIGN_QUERY_KEY } = campaignApi;

async function fetchCampaignList(State: CustomerDisplayState, PersistedState: CustomerDisplayPersistedState) {
  const { selectedOutletId: outletId, page } = State;
  const { status } = PersistedState;

  return queryClient.fetchQuery({
    queryKey: [CAMPAIGN_QUERY_KEY, { outletId, status, page }],
    queryFn: () =>
      campaignApi.getCampaignList({
        outletId,
        status,
        page,
        perPage: PER_PAGE,
      }),
  });
}

async function setCampaigns(
  data: CampaignModel.List,
  State: CustomerDisplayState,
  PersistedState: CustomerDisplayPersistedState,
  isReplace?: boolean,
) {
  if (isReplace) {
    await Promise.all([PersistedState.campaignIds.clear(), PersistedState.campaignNames.clear(), State.campaigns.clear()]);
  }

  data.forEach((campaign) => {
    PersistedState.campaignIds.add(campaign.id);
    PersistedState.campaignNames.set(campaign.id, campaign.name);
    State.campaigns.set(campaign.id, {
      ...campaign,
      status: getCampaignStatus(campaign),
      isLoading: false,
    });
  });

  if (data.length === PER_PAGE) {
    State.canFetchMore = true;
  }
}

async function refetchCampaignList(State: CustomerDisplayState, PersistedState: CustomerDisplayPersistedState) {
  State.page = 0;

  const data = await fetchCampaignList(State, PersistedState);

  setCampaigns(data, State, PersistedState, true);
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function setInitialListFromPersistedData(State: CustomerDisplayState, PersistedState: CustomerDisplayPersistedState) {
  // PersistedState.campaignIds.forEach((id) => {
  //   if (!State.campaigns.has(id)) {
  //     State.campaigns.set(id, {
  //       isLoading: true,
  //       name: PersistedState.campaignNames.get(id),
  //     });
  //   }
  // });
}

async function campaignCreatorHandler(data: CampaignModel.CreateRequest) {
  return campaignApi.createCampaign(data);
}

async function campaignStatusUpdatorHandler(id: DetailedCampaign['id'], isActive: boolean) {
  return campaignApi.updateCampaignStatus({ campaignId: id, campaignStatus: { is_active: isActive } });
}

async function campaignDeletionHandler(id: DetailedCampaign['id']) {
  return campaignApi.deleteCampaign(id);
}

async function campaignUpdatorHandler(data: CampaignModel.EditRequest) {
  return campaignApi.editCampaign(data);
}

const { proxy: PersistedState } = withPersist<CustomerDisplayPersistedState>(
  {
    campaignIds: new Set(),
    campaignNames: new Map(),
    status: 'all',
  },
  'customer-display',
  {
    expiry: 1000 * 60 * 60 * 24, // 1 day expiry
    session: 'user', // removed on logout
  },
);

const State: CustomerDisplayState = proxy({
  page: 0,
  selectedOutletId: undefined,
  campaigns: proxyMap<number, DetailedCampaignWithStatusAndLoading>(),
  creatingCampaigns: proxyMap<string, DetailedCampaignWithStatusAndLoading>(),
  canFetchMore: true,
  isLoading: false,
  modalData: undefined,
  async onFetchMore() {
    if (State.canFetchMore) {
      State.page += 1;
      State.canFetchMore = false;

      const data = await fetchCampaignList(State, PersistedState);

      setCampaigns(data, State, PersistedState);
    }
  },
  async onChangeSelectedOutletId(outlet) {
    State.selectedOutletId = outlet.id;

    refetchCampaignList(State, PersistedState);
  },
  async onCreateCampaign(data, handler = campaignCreatorHandler) {
    try {
      State.isLoading = true;

      const result = await handler(data);

      refetchCampaignList(State, PersistedState);

      return result;
    } catch (e) {
      console.warn('Encounter an error when trying to create campaign');

      throw e;
    } finally {
      State.isLoading = false;
    }
  },
  async onDeactivateCampaign(id, handler = campaignStatusUpdatorHandler) {
    try {
      State.isLoading = true;

      await handler(id, false);

      refetchCampaignList(State, PersistedState);
    } catch (e) {
      console.warn('Encounter an error when trying to deactivate campaign');

      throw e;
    } finally {
      State.isLoading = false;
    }
  },
  async onDeleteCampaign(id, handler = campaignDeletionHandler) {
    try {
      State.isLoading = true;

      await handler(id);

      refetchCampaignList(State, PersistedState);
    } catch (e) {
      console.warn('Encounter an error when trying to delete campaign');

      throw e;
    } finally {
      State.isLoading = false;
    }
  },
  async onUpdateCampaign(data, handler = campaignUpdatorHandler) {
    try {
      State.isLoading = true;

      const result = await handler(data);

      refetchCampaignList(State, PersistedState);

      return result;
    } catch (e) {
      console.warn('Encounter an error when trying to update campaign');

      throw e;
    } finally {
      State.isLoading = false;
    }
  },
  async onReactivateCampaign(id, handler = campaignStatusUpdatorHandler) {
    try {
      State.isLoading = true;

      await handler(id, true);

      refetchCampaignList(State, PersistedState);
    } catch (e) {
      console.warn('Encounter an error when trying to reactivate campaign');

      throw e;
    } finally {
      State.isLoading = false;
    }
  },
  async onReactivateAndUpdateCampaign(data) {
    try {
      State.isLoading = true;

      await campaignStatusUpdatorHandler(data.campaignId, true);

      const result = await campaignUpdatorHandler(data);

      refetchCampaignList(State, PersistedState);

      return result;
    } catch (e) {
      console.warn('Encounter an error when trying to reactivate campaign');

      throw e;
    } finally {
      State.isLoading = false;
    }
  },
  async onChangeFilter(status) {
    PersistedState.status = status;

    refetchCampaignList(State, PersistedState);
  },
  useOutletDetails(outletIds) {
    const [outlets, setOutlets] = useState<(ProfileModel.Outlet | undefined)[]>([]);
    const { data, status } = profileApi.useProfile();

    useEffect(() => {
      if (status === 'success') {
        setOutlets(outletIds.map((oId) => data.outlets?.find((o) => o.id === oId)));
      } else {
        setOutlets([]);
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status, ...outletIds]);

    return {
      status,
      data: outlets,
    };
  },
  useData() {
    // Load the list
    const [enabled, setEnabled] = useState(true);

    const { data, isFetched, status } = useQuery({
      queryKey: [CAMPAIGN_QUERY_KEY, { page: 0 }],
      queryFn: () =>
        campaignApi.getCampaignList({
          perPage: PER_PAGE,
          page: 0,
        }),
      onSuccess(res) {
        setCampaigns(res, State, PersistedState);

        setEnabled(false);
      },
      enabled,
    });

    useEffect(() => {
      // Load data from persisted into state
      setInitialListFromPersistedData(State, PersistedState);
    }, []);

    return {
      data: data ?? [],
      isFetched,
      status,
    };
  },
});

function openModal(
  outletIds: number[],
  errorOutlets: number[],
  mappedErrorOutlets: (string | undefined)[],
  handleChangeOutlets: Exclude<CustomerDisplayState['modalData'], undefined>['handleChangeOutlets'],
) {
  State.modalData = {
    outletIds,
    errorOutlets,
    mappedErrorOutlets,
    handleChangeOutlets,
  };
}

function closeModal() {
  State.modalData = undefined;
}

export {
  type DetailedCampaign,
  type DetailedCampaignWithStatus,
  type DetailedCampaignWithStatusAndLoading,
  type DetailedCampaignWithStatusAndLoadingFalse,
  type DetailedCampaignImage,
  type CustomerDisplayState,
  type CustomerDisplayPersistedState,
  State,
  openModal,
  closeModal,
  PersistedState as _PersistedState,
  setCampaigns as _setCampaigns,
  setInitialListFromPersistedData as _setInitialListFromPersistedData,
  // campaignCreatorHandler as _campaignCreatorHandler,
  // campaignDeactivationHandler as _campaignDeactivationHandler,
  // campaignDeletionHandler as _campaignDeletionHandler,
  // campaignUpdatorHandler as _campaignUpdatorHandler,
};
